import React, { useState, useEffect } from "react";
import axios from "axios";

function PromoteSession() {
  const storedSessionId = localStorage.getItem("currentSessionId");

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClass, setFilterClass] = useState("");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [targetSession, setTargetSession] = useState("");
  const [targetClass, setTargetClass] = useState("");
  const [targetSection, setTargetSection] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // --- Fetch students of CURRENT session ---
  useEffect(() => {
    async function fetchStudents() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
          params: { sessionId: storedSessionId },
        });
        const activeStudents = res.data.filter((s) => s.status === "active");
        setStudents(activeStudents);
        setFilteredStudents(activeStudents);
      } catch (err) {
        console.error("Error fetching students:", err);
      }
    }

    fetchStudents();
  }, [storedSessionId]);

  // --- Fetch all sessions ---
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/sessions/allSessions`)
      .then((res) => setSessions(res.data))
      .catch((err) => console.error("Error fetching sessions:", err));
  }, []);

  // --- Fetch classes based on TARGET session ---
  useEffect(() => {
    if (!targetSession) {
      setClasses([]);
      setTargetClass("");
      setSections([]);
      setTargetSection("");
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses`, { params: { sessionId: targetSession } })
      .then((res) => {
        setClasses(res.data);
        setTargetClass("");
        setTargetSection("");
      })
      .catch((err) => console.error("Error fetching classes:", err));
  }, [targetSession]);

  // --- Fetch sections based on TARGET class and TARGET session ---
  useEffect(() => {
    if (!targetClass || !targetSession) {
      setSections([]);
      setTargetSection("");
      return;
    }

    axios
      .get(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(targetClass)}/getSectionsByClassName`,
        { params: { sessionId: targetSession } }
      )
      .then((res) => setSections(Array.isArray(res.data) ? res.data : res.data.sections || []))
      .catch((err) => {
        console.error("Error fetching sections:", err);
        setSections([]);
      });
  }, [targetClass, targetSession]);

  // --- Search and filter students ---
const handleSearch = () => {
  let temp = students.filter((s) => s.status === "active");

  if (searchTerm) {
    temp = temp.filter(
      (s) =>
        s.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.rollNo.toString().includes(searchTerm)
    );
  }

  if (filterClass) {
    temp = temp.filter((s) => s.class === filterClass);
  }

  // ✅ ADD SECTION FILTER
  if (filterSection) {
    temp = temp.filter((s) => s.section === filterSection);
  }

  setFilteredStudents(temp);
};

const handleReset = () => {
  setSearchTerm("");
  setFilterClass("");
  setFilterSection(""); // ✅ add this
  setFilteredStudents(students.filter((s) => s.status === "active"));
  setSelectedStudentIds([]);
  setSelectAll(false);
};
  // --- Checkbox logic ---
  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map((s) => s._id));
    }
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (id) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  // --- Promote students ---
  const handlePromote = async () => {
    if (selectedStudentIds.length === 0) {
      alert("Select at least one student.");
      return;
    }

    if (!targetClass || !targetSection || !targetSession) {
      alert("Select Class, Section & Session.");
      return;
    }

    const studentsData = selectedStudentIds.map((id) => ({
      id,
      class: targetClass,
      section: targetSection,
      session: targetSession,
    }));

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/updateMultipleStudentsClassAndSection`,
        { students: studentsData },
        {
          headers:{ Authorization: `Bearer ${localStorage.getItem("token")}` }
        }
      );

      alert("Students promoted successfully!");

      // Update frontend state
      setStudents((prev) =>
        prev.map((s) =>
          selectedStudentIds.includes(s._id)
            ? { ...s, class: targetClass, section: targetSection }
            : s
        )
      );

      setFilteredStudents((prev) =>
        prev.map((s) =>
          selectedStudentIds.includes(s._id)
            ? { ...s, class: targetClass, section: targetSection }
            : s
        )
      );

      // Clear selections
      setSelectedStudentIds([]);
      setSelectAll(false);
      setTargetClass("");
      setTargetSection("");
      setTargetSession("");
    } catch (err) {
      console.error(err);
      alert("Promotion failed.");
    }
  };

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h2 className="text-3xl font-bold mb-4">Promote Students</h2>
      <hr className="mb-6" />

      {/* Search & Filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <div>
          <label className="font-semibold text-gray-700 mb-1 block">Search by Name / Roll No</label>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        <div>
          <label className="font-semibold text-gray-700 mb-1 block">Filter by Class</label>
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">All Classes</option>
            {Array.from(new Set(students.map((s) => s.class))).map((cls) => (
              <option key={cls} value={cls}>
                {cls}
              </option>
            ))}
          </select>
        </div>
       <div>
  <label className="font-semibold text-gray-700 mb-1 block">
    Filter by Section
  </label>
  <select
    value={filterSection}
    onChange={(e) => setFilterSection(e.target.value)}
    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
  >
    <option value="">All Sections</option>

    {Array.from(new Set(students.map((s) => s.section))).map((sec) => (
      <option key={sec} value={sec}>
        {sec}
      </option>
    ))}
  </select>
</div>

        <div className="flex items-end gap-4">
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Search
          </button>
          <button
            onClick={handleReset}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Students Table */}
      <div className="overflow-x-auto border rounded-lg mb-6">
        <table className="min-w-full bg-white border">
          <thead className="bg-gray-200 text-gray-800">
            <tr>
              <th className="py-2 px-4 border">
                <input type="checkbox" checked={selectAll} onChange={handleSelectAllChange} />
              </th>
              <th className="py-2 px-4 border">Name</th>
              <th className="py-2 px-4 border">Father Name</th>
              <th className="py-2 px-4 border">Roll No</th>
              <th className="py-2 px-4 border">Class</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((s) => (
              <tr key={s._id} className="hover:bg-gray-100 text-center">
                <td className="py-2 px-4 border">
                  <input
                    type="checkbox"
                    checked={selectedStudentIds.includes(s._id)}
                    onChange={() => handleCheckboxChange(s._id)}
                  />
                </td>
                <td className="py-2 px-4 border">{s.firstName} {s.lastName}</td>
                <td className="py-2 px-4 border">{s.fatherName}</td>
                <td className="py-2 px-4 border">{s.rollNo}</td>
                <td className="py-2 px-4 border">{s.class} ({s.section})</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Promotion Section */}
      <h3 className="text-2xl font-semibold mb-3">Promotion Details</h3>
      <hr className="mb-5" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="font-semibold text-gray-700 mb-1 block">Promote to Session</label>
          <select
            value={targetSession}
            onChange={(e) => setTargetSession(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Select Session</option>
            {sessions.map((sess) => (
              <option key={sess._id} value={sess._id}>{sess.sessionName}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="font-semibold text-gray-700 mb-1 block">Promote to Class</label>
          <select
            value={targetClass}
            onChange={(e) => setTargetClass(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
            disabled={!targetSession}
          >
            <option value="">Select Class</option>
            {classes.map((cls) => (
              <option key={cls._id} value={cls.className}>{cls.className}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="font-semibold text-gray-700 mb-1 block">Promote to Section</label>
          <select
            value={targetSection}
            onChange={(e) => setTargetSection(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
            disabled={!targetClass}
          >
            <option value="">Select Section</option>
            {sections.map((sec) => (
              <option key={sec._id} value={sec.sectionName}>{sec.sectionName}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={handlePromote}
        className="mt-6 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
      >
        Promote Selected Students
      </button>
    </div>
  );
}

export default PromoteSession;
